import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  Divider,
  Chip,
  Paper,
  Avatar,
  Fade
} from "@mui/material";
import { Search, Filter, LayoutGrid, Receipt, Landmark, Sparkles, Rocket } from "lucide-react";
import logoGeneral from "../../identidad/Logo.png";
import banner from "../General/banner.png"; // Asegúrate de que la ruta sea correcta

const apiUrl = process.env.REACT_APP_API_BASE_URL;

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  pearl: "#EEF3F4",
  crema: "#F3F2B3",
  aqua: "#00C0CA",
  lime: "#8FC440",
  teal: "#008B9C",
  azur: "#28708B",
  dark: "#22404D",
  red: "#F65F4E"
};

export default function DashboardUnificado() {
  const navigate = useNavigate();
  const [programas, setProgramas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState("2026");

  useEffect(() => {
    if (localStorage.getItem("rol") !== "finanzas") {
      navigate("/");
      localStorage.clear();
    }
  }, [navigate]);

  useEffect(() => {
    const fetchProgramas = async () => {
      try {
        const response = await fetch(`${apiUrl}/Programa/Get`, {
          headers: {
            'Content-Type': 'application/json',
            'apiKey': localStorage.getItem("apiKey")
          }
        });
        if (response.ok) {
          const data = await response.json();
          setProgramas(data.sort((a, b) => a.nombre.localeCompare(b.nombre)));
        }
      } catch (error) {
        console.error("Error al cargar programas", error);
      }
    };
    fetchProgramas();
  }, []);

  const extraerAnio = (nombre) => {
    const match = nombre.match(/\d{4}/);
    return match ? match[0] : "S/A";
  };

  const añosDisponibles = ["Todos", ...new Set(programas.map(p => extraerAnio(p.nombre)))].sort().reverse();

  const filteredProgramas = programas.filter((p) => {
    const cumpleNombre = p.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const cumpleAnio = selectedYear === "Todos" || extraerAnio(p.nombre) === selectedYear;
    return cumpleNombre && cumpleAnio;
  });

  return (
    <Box sx={{ 
      p: { xs: 2, md: 6 }, 
      pt: { xs: 10, md: 14 }, 
      backgroundColor: "#FFFFFF", 
      minHeight: "100vh" 
    }}>
      
      {/* BANNER ESTILO SOFT HUES */}
      <Paper elevation={0} sx={{ 
        position: 'relative', mb: 6, borderRadius: '32px', overflow: 'hidden', 
        height: { xs: 180, md: 240 },
        border: `1px solid ${PALETTE.sky}`
      }}>
        <Box component="img" src={banner} alt="Banner" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <Box sx={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          background: `linear-gradient(90deg, ${PALETTE.dark}E6 0%, ${PALETTE.dark}40 100%)`,
          display: 'flex', flexDirection: 'column', justifyContent: 'center', p: { xs: 3, md: 6 }
        }}>
          <Typography variant="overline" sx={{ color: PALETTE.ice, fontWeight: 800, letterSpacing: 3 }}>
            ÁREA DE FINANZAS
          </Typography>
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 900, fontSize: { xs: '2rem', md: '3.5rem' }, mb: 1 }}>
            Panel de Control 
          </Typography>
          <Typography variant="h6" sx={{ color: 'white', opacity: 0.8, fontWeight: 400 }}>
            Bienvenida, {localStorage.getItem("nombre") || "Usuario"}
          </Typography>
        </Box>
      </Paper>

      {/* Acciones Globales */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        <Grid item xs={12} sm={6}>
          <Paper 
            elevation={0}
            onClick={() => navigate(`/adelantos`)}
            sx={{ 
              p: 3.5, cursor: 'pointer', borderRadius: '28px',
              backgroundColor: PALETTE.pearl,
              transition: '0.4s ease',
              border: '1px solid transparent',
              '&:hover': { backgroundColor: 'white', borderColor: PALETTE.aqua, boxShadow: `0 20px 40px ${PALETTE.ice}`, transform: 'translateY(-5px)' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ bgcolor: 'white', color: PALETTE.aqua, width: 55, height: 55, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                <Landmark size={24} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: PALETTE.dark }}>Adelantos</Typography>
               
              </Box>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper 
            elevation={0}
            onClick={() => navigate(`/gastos`)}
            sx={{ 
              p: 3.5, cursor: 'pointer', borderRadius: '28px',
              backgroundColor: PALETTE.crema + "60",
              transition: '0.4s ease',
              border: '1px solid transparent',
              '&:hover': { backgroundColor: 'white', borderColor: PALETTE.lime, boxShadow: `0 20px 40px ${PALETTE.crema}`, transform: 'translateY(-5px)' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ bgcolor: 'white', color: PALETTE.lime, width: 55, height: 55, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
                <Receipt size={24} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: PALETTE.dark }}>Caja Chica</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* BUSCADOR MEDIANO Y CENTRADO */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 8 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            px: 3, py: 1, 
            borderRadius: '50px', 
            border: `2px solid ${PALETTE.ice}`,
            backgroundColor: '#fff',
            width: { xs: '100%', md: '700px' }, // Tamaño mediano aumentado
            boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
            transition: '0.3s',
            '&:focus-within': { borderColor: PALETTE.aqua, boxShadow: `0 10px 30px ${PALETTE.ice}` }
          }}
        >
          <Search size={22} color={PALETTE.teal} />
          <TextField
            fullWidth
            variant="standard"
            placeholder="Escribe el nombre de un programa para buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ disableUnderline: true, sx: { px: 2, fontSize: '1rem', color: PALETTE.dark, fontWeight: 500 } }}
          />
          <Divider sx={{ height: 30, mx: 2 }} orientation="vertical" />
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disableUnderline
              sx={{ fontSize: '0.9rem', fontWeight: 700, color: PALETTE.teal }}
              startAdornment={<Filter size={16} style={{ marginRight: 8, color: PALETTE.sky }} />}
            >
              {añosDisponibles.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      </Box>

      {/* Listado de Programas */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
         <Rocket size={24} color={PALETTE.aqua} />
         <Typography variant="h5" sx={{ fontWeight: 800, color: PALETTE.dark }}>Programas Disponibles</Typography>
         <Chip label={filteredProgramas.length} sx={{ bgcolor: PALETTE.dark, color: 'white', fontWeight: 700 }} />
      </Box>

      <Grid container spacing={3}>
        {filteredProgramas.map((item) => (
          <Grid item xs={6} sm={4} md={3} key={item.id}>
            <Fade in={true}>
              <Card
                elevation={0}
                onClick={() => navigate(`/DashboardPrograma/${item.id}`)}
                sx={{
                  height: '100%',
                  p: 3,
                  cursor: "pointer",
                  borderRadius: '28px',
                  backgroundColor: 'white',
                  border: `1px solid #f0f0f0`,
                  transition: "0.3s",
                  "&:hover": { borderColor: PALETTE.sky, transform: "translateY(-8px)", boxShadow: '0 15px 30px rgba(0,0,0,0.05)' },
                }}
              >
                <Box sx={{ height: 110, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fafafa', borderRadius: '20px', p: 2 }}>
                  <img
                    src={`data:${item.logoMimeType};base64,${item.logo}`}
                    alt={item.nombre}
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: 'contain' }}
                  />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: PALETTE.dark, textAlign: 'center', lineHeight: 1.2 }}>
                  {item.nombre.slice(0, -4)}
                </Typography>
                <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                  <Chip label={extraerAnio(item.nombre)} size="small" sx={{ bgcolor: PALETTE.ice, color: PALETTE.azur, fontWeight: 700 }} />
                </Box>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}